import { Description } from '@casavo/base-ui'
import useTranslation from 'next-translate/useTranslation'
import React, { useState } from 'react'

import { useDevice } from '@/context/Device'
import { AccordionContent, AccordionItem, AccordionRoot, AccordionTrigger } from '@/design-lib/components/Accordion'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { ListingCate } from '@/page-commons/listingBySlug'
import { StateAtDeed } from '@/types/graphql-generated-types'
import { formatDate } from '@/utils/date'
import { formatPrice } from '@/utils/format-currency'
import { handleListingDetailClick } from '@/utils/tracking/eventsHandlers'

import { detailInfoRow } from './styles.css'

type Props = {
  listing: ListingCate
}

const AccordionDetailInfo: React.FC<Props> = ({ listing }) => {
  const { lang, t } = useTranslation()
  const device = useDevice()
  const dateFormat = device === 'mobile' ? 'short' : 'long'
  const [isOpening, setIsOpening] = useState<boolean>(false)

  const listingCreationDate =
    listing.publishedAt !== undefined ? formatDate(dateFormat, lang, listing.publishedAt) : undefined
  const listingUpdateDate =
    listing.updatedAt !== undefined ? formatDate(dateFormat, lang, listing.updatedAt) : undefined

  const lastUpdateDate = listingUpdateDate || listingCreationDate

  const detailInfo = [
    {
      condition: listing.additionalInfo.kitchen,
      label: 'kitchen',
      value: t(`listing-detail:kitchen_${listing.additionalInfo.kitchen}`),
    },
    {
      condition: listing.additionalInfo.glazing,
      label: 'glazing',
      value: t(`listing-detail:glazing_${listing.additionalInfo.glazing}`),
    },
    {
      condition: listing.additionalInfo.furnishings,
      label: 'furniture',
      value: t(`listing-detail:${listing.additionalInfo.furnishings}`),
    },
    {
      condition: listing.additionalInfo.lastRenovationYear,
      label: 'year_of_renovation',
      value: listing.additionalInfo.lastRenovationYear,
    },
    {
      condition: listing.additionalInfo.constructionYear,
      label: 'year_of_construction',
      value: listing.additionalInfo.constructionYear,
    },
    {
      condition: listing.additionalInfo.condoFeesPerYearCate && listing.additionalInfo.condoFeesPerYearCate.cents,
      label: 'condo_fees',
      value: `${formatPrice(listing.additionalInfo.condoFeesPerYearCate, lang)} ${t('listing-detail:per_year_label')}`,
    },
    {
      condition: listing.additionalInfo.stateAtDeed && listing.additionalInfo.stateAtDeed !== StateAtDeed.Unknown,
      label: 'state_at_deed',
      value: t(`listing-detail:${listing.additionalInfo.stateAtDeed}`),
    },
    {
      condition: listing.additionalInfo.exposure && listing.additionalInfo.exposure.length > 0,
      label: 'exposure',
      value:
        listing.additionalInfo.exposure &&
        listing.additionalInfo.exposure.map((exposure) => t(`listing-detail:${exposure}`)).join(', '),
    },
    {
      condition: listing.additionalInfo.views && listing.additionalInfo.views.length > 0,
      label: 'view',
      value:
        listing.additionalInfo.views &&
        listing.additionalInfo.views.map((view) => t(`listing-detail:view_${view}`)).join(', '),
    },
    {
      condition: listing.additionalInfo.heatingSystem,
      label: 'heating_system',
      value: t(`listing-detail:${listing.additionalInfo.heatingSystem}`),
    },
    {
      condition: listing.additionalInfo.coolingSystem && listing.additionalInfo.coolingSystem !== 'NONE',
      label: 'coolingSystem_full',
      value: t(`listing-detail:coolingSystem_${listing.additionalInfo.coolingSystem}`),
    },
    {
      condition: listing.additionalInfo.energyRating && listing.additionalInfo.energyRating !== 'NONE',
      label: 'energy_rating_label',
      value: t(`listing-detail:energy_rating.energy_${listing.additionalInfo.energyRating?.toLowerCase()}`),
    },
    {
      condition: listing.additionalInfo.energyPerformanceIndex,
      label: 'energy_performance_index_label',
      value: `${listing.additionalInfo.energyPerformanceIndex} ${t('listing-detail:energy_performance_index_udm')}`,
    },
    {
      condition: listing.additionalInfo.estateRegistryClass && listing.additionalInfo.estateRegistryClass !== 'NONE',
      label: 'estate_registry_class',
      value: listing.additionalInfo.estateRegistryClass,
    },
    {
      condition: lastUpdateDate,
      label: 'last_update',
      value: lastUpdateDate,
    },
  ]

  return (
    <AccordionRoot
      defaultOpenedItem=""
      onToggle={(value) => {
        setIsOpening(value === 'characteristics')
        value === 'characteristics'
          ? handleListingDetailClick('ShowAllCharacteristics')
          : handleListingDetailClick('ShowLessCharacteristics')
      }}
    >
      <AccordionItem name="characteristics">
        <AccordionContent>
          <div
            className={sprinkles({
              display: 'flex',
              flexDirection: 'column',
              paddingTop: 'xl',
            })}
          >
            {detailInfo.map((info, index) =>
              info.condition ? (
                <div key={index} className={detailInfoRow}>
                  <Description>{t(`listing-detail:${info.label}`)}</Description>
                  <Description className={sprinkles({ textAlign: 'right' })} color="greyscale600">
                    {info.value}
                  </Description>
                </div>
              ) : null
            )}
          </div>
        </AccordionContent>
        <AccordionTrigger>
          {t(isOpening ? 'listing-detail:see_less_characteristics' : 'listing-detail:see_all_characteristics')}
        </AccordionTrigger>
      </AccordionItem>
    </AccordionRoot>
  )
}
export default AccordionDetailInfo
